import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { getToken } from "../utils/tokenManager";
import LoyaltyCard from "../pages/LoyaltyCard";
import { motion, AnimatePresence } from "framer-motion";

const LoyaltyPromoPopup = () => {
  const [show, setShow] = useState(false);
  const [hasActiveCard, setHasActiveCard] = useState(true); // Default to true to avoid flashing
  const { authState } = useAuth();
  const { isAuthenticated, authType } = authState;
  const navigate = useNavigate();

  useEffect(() => {
    let timeoutId;

    const checkLoyaltyCard = async () => {
      // Only check for logged-in 'user' types
      if (isAuthenticated && authType === "user") {
        try {
          const token = getToken();
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/user/card`,
            {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          if (response.ok) {
            const data = await response.json();
            if (data.card_number) {
              setHasActiveCard(true);
            } else {
              setHasActiveCard(false);
              // If no card and not dismissed in this session, start the timer
              if (!sessionStorage.getItem("loyalty_promo_dismissed")) {
                timeoutId = setTimeout(() => {
                  setShow(true);
                }, 2000);
              }
            }
          }
        } catch (error) {
          console.error("Error fetching loyalty card status:", error);
        }
      }
    };

    checkLoyaltyCard();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isAuthenticated, authType]);

  // Clear dismissed flag on logout to allow it to show on next login
  useEffect(() => {
    if (!isAuthenticated) {
      sessionStorage.removeItem("loyalty_promo_dismissed");
      setShow(false);
    }
  }, [isAuthenticated]);

  const handleClose = () => {
    setShow(false);
    sessionStorage.setItem("loyalty_promo_dismissed", "true");
  };

  const handleJoinNow = () => {
    setShow(false);
    sessionStorage.setItem("loyalty_promo_dismissed", "true");
    navigate("/payment-gateway");
  };

  if (!show || hasActiveCard) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-lg bg-gradient-to-br from-[#47001C] to-[#971132] rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] overflow-hidden border border-white/10"
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
          >
            <X size={20} />
          </button>

          <div className="p-6 md:p-8 flex flex-col items-center text-center">
            {/* Animated Card Preview */}
            <div className="w-full mb-6 transform scale-90 sm:scale-100 transition-transform duration-500 hover:scale-105">
              <LoyaltyCard />
            </div>

            {/* Content */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-[#fbd784] tracking-tight">
                Social Loyalty Card
              </h2>
              <p className="text-white/90 text-sm md:text-base leading-relaxed px-4">
                Join our community and make a difference while enjoying exclusive benefits
              </p>

              <div className="py-4">
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-4xl font-bold text-white">₹1000</span>
                  <span className="text-xl text-white/70">/ 1 Year</span>
                </div>
              </div>

              {/* Join Now Button */}
              <button
                onClick={handleJoinNow}
                className="w-full md:w-64 bg-[#fbd784] hover:bg-[#ffe085] text-[#47001C] font-bold py-4 rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 uppercase tracking-wide"
              >
                JOIN NOW
              </button>

            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default LoyaltyPromoPopup;
